import * as React from "react";
import { SetupChecklist, type SetupChecklistTask } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// A plain anchor stand-in for a router <Link>. The real consumer passes their
// framework link (react-router's <Link>, Next's <Link>, etc.); the library
// itself imports no router.
function DemoLink({
  to,
  href,
  children,
  ...rest
}: {
  to?: string;
  href?: string;
  children?: React.ReactNode;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a href={to ?? href} {...rest}>
      {children}
    </a>
  );
}

const TASKS: SetupChecklistTask[] = [
  { id: "profile", label: "Complete your profile", completed: true },
  { id: "studio", label: "Create your first Studio", completed: true },
  { id: "invite", label: "Invite a teammate", completed: false, href: "/team/invite" },
  {
    id: "publish",
    label: "Publish an experience",
    completed: false,
    href: "/studios/new",
    description: "Ship something your audience can open.",
  },
];

export function SetupChecklistDemo() {
  // Interactive block: SetupChecklist is fully controlled by `tasks`, so the
  // demo owns the array and flips `completed` on click. Toggling the final
  // remaining task crosses 100% and fires the celebrate-on-complete moment.
  const [tasks, setTasks] = React.useState<SetupChecklistTask[]>(() =>
    TASKS.map((t) => ({ ...t, href: undefined })),
  );
  const toggle = (id: string) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)));

  return (
    <Section
      id="setup-checklist"
      title="SetupChecklist"
      description="Collapsible onboarding checklist with an animated progress bar, motion rows, a two-tap dismiss guard, and a celebrate-on-complete moment. Consolidates the Creator Studio / FE-React forks onto shipped primitives (Progress, Collapsible, Tooltip)."
    >
      <VariantBlock
        label="Default"
        caption="two of four done, incomplete rows navigate via the injected link"
      >
        <div className="w-full max-w-md">
          <SetupChecklist
            title="Jumpstart Your AISquare Journey"
            description="Complete this checklist to unlock the full AISquare experience"
            tasks={TASKS}
            linkComponent={DemoLink}
            onDismiss={() => {}}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Interactive"
        caption="toggle rows; finishing the last one fires the completion moment"
      >
        <div className="flex w-full max-w-md flex-col gap-3">
          <SetupChecklist tasks={tasks} celebrateIntensity="burst" />
          <div className="flex flex-wrap gap-2">
            {tasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className="rounded-pill border border-hairline bg-surface px-3 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
              >
                {t.completed ? "Undo" : "Done"}: {t.label}
              </button>
            ))}
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="States"
        caption="server-computed percent, compact size, a loading card, and an empty-checklist embed (renders nothing)"
      >
        <div className="flex w-full max-w-md flex-col gap-6">
          <SetupChecklist
            title="Server-computed progress"
            description="percentComplete overrides the derived count"
            tasks={TASKS}
            percentComplete={90}
            celebrateOnComplete={false}
          />
          <SetupChecklist size="compact" description="Compact density for sidebars" tasks={TASKS} />
          {/* loading and empty are BOTH `tasks={[]}` — the flag is the only
              difference, and it has to win. Without it the card is absent
              during the fetch and then pops in at full height, shoving
              whatever the user was reaching for down the page. */}
          <SetupChecklist
            tasks={[]}
            loading
            title="Loading"
            description="Placeholder rows carry the real row geometry: h-6 circle, label, description."
          />
          <SetupChecklist tasks={[]} />
        </div>
      </VariantBlock>
    </Section>
  );
}
